import style from './messages.module.scss';

import React, { useMemo } from 'react';
import useMessages from '../../hooks/useMessages';

import useApp from 'hooks/useApp';

import StartChatButton from './StartChat/index';
import useRenderMessage from './useRenderMessage';
import TranslationOn from 'components/TranslationOn';
import useToggleTranslate from 'hooks/useToggleTranslate';
import classNames from 'classnames';
import StartChatMessage from './StartChatMessage';
import BotTypingIndicator from 'components/BotTypingIndicator';
import NewMessageIndicator from './NewMessageIndicator';
import { useNewMessageIndicator } from './NewMessageIndicator/useNewMessageIndicator';
import AgentTypingIndicator from 'components/AgentTypingIndicator';
import useRenderBanner from './useRenderBanner';
import { DEFAULT_LANGUAGE } from 'translations';

interface Props { }

const Messages: React.FC<Props> = () => {
    const messages = useMessages();
    const renderMessage = useRenderMessage();

    const renderBanner = useRenderBanner();

    const { isBotTyping, ended, showStartNewChatButton, translation, globalRestoreSession, globalPageStatus, connectedToAgent, appTextTranslations, language } = useApp();

    const { messagesContainer, newMessageCount, onClickNewMessageIndicator } = useNewMessageIndicator();

    const toggleTranslate = useToggleTranslate();

    const showTranslationToolbar = !!translation?.language && translation.language !== DEFAULT_LANGUAGE && !ended && connectedToAgent;

        const findLastCustomerMessageIndex = () => {
            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i]?.role === 'CUSTOMER') {
                    return i;
                }
            }
            return -1;
        };

        const lastCustomerMessageIndex = findLastCustomerMessageIndex();

        const updatedMessages = useMemo(() => {
            return messages.map((message, index) => {
                const isLastCustomerMessage =
                    index === lastCustomerMessageIndex &&
                    message.role === 'CUSTOMER';
                return {
                    ...message,
                    lastCustomerMessage: isLastCustomerMessage,
                };
            });
        }, [messages, lastCustomerMessageIndex]);

    const startChatElement = () =>
        (!globalRestoreSession || !globalPageStatus) ? <StartChatButton /> : <StartChatMessage />
        
    const newMessageText = appTextTranslations?.newMessageFromChat?.[language] ?? appTextTranslations?.newMessageFromChat?.[DEFAULT_LANGUAGE];
    return (
        <main ref={messagesContainer} className={style.container}>
            <span className={style.screenReaderOnly} role="status" tabIndex={-1}>
                {
                    // if most recent message is not from CUSTOMER, inform screen reader of new message - alternating full stop to make screen reader reannounce if another comes in
                    messages.length && messages[messages.length - 1]?.role !== 'CUSTOMER'
                        ? `${newMessageText}${messages.length % 2 === 0 ? '.' : ''}`
                        : ''
                }
            </span>

            {showTranslationToolbar && (
                <TranslationOn
                    language={translation.language!}
                    on={translation.on}
                    onTurnOff={() => toggleTranslate(translation.language, !translation.on)}
                />
            )}

            <div
                className={classNames({
                    [style.messagesContainer]: true,
                    [style.toolbarTop]: showTranslationToolbar,
                })}
                aria-live="polite"
            >
                <div className={style.bannersContainer}>
                    {renderBanner(messages)}
                </div>

                {updatedMessages.map((message, index) =>
                    renderMessage(message, index === messages.length - 1)
                )}

                {isBotTyping && <BotTypingIndicator />}
            </div>

            {ended && showStartNewChatButton && startChatElement()}

            {newMessageCount > 0 && (
                <NewMessageIndicator
                    newMessages={newMessageCount}
                    onClick={onClickNewMessageIndicator}
                />
            )}

            <AgentTypingIndicator />
        </main>
    );
};

export default Messages;
